/**
 * Turning a user question into the text(s) actually sent to the vector store.
 *
 * Split out of `../query-enhancer.ts`. Everything here produces a STRING that
 * goes straight into an embedding, so the assertions that protect it are on
 * exact bytes: a stray comma or a reordered clause moves the vector and quietly
 * degrades retrieval with nothing logged and nothing failing.
 */

import { TOPIC_KEYWORDS } from './keywords';
import { detectQueryTopic } from './topic';

/**
 * Enhance a user query with strategic keywords for better embedding retrieval
 *
 * This function:
 * 1. Adds WTC team member context
 * 2. Ensures Hub operations focus (sales, operations, tickets)
 * 3. Includes actionable guidance context
 * 4. Adds topic-specific keywords
 * 5. Maintains the original query intent
 *
 * @param query - The original user query
 * @param userId - The user ID (context for the caller; it does NOT affect the string)
 * @param locale - The language locale (default: 'en')
 * @returns Enhanced query with strategic keywords
 *
 * @example
 * // Original: "How do I follow up with leads?"
 * // Enhanced: "As a WTC team member, how do I follow up with leads related to sales support and follow-up leads, based on actionable guidance and MSP best practices"
 */
export function enhanceQueryWithContext(
  query: string,
  userId?: string,
  locale: string = 'en'
): string {
  const topic = detectQueryTopic(query);
  const components: string[] = [];

  // Team context.
  if (locale === 'es') {
    components.push('Como miembro del equipo de West Tennessee Consulting');
  } else {
    components.push('As a WTC team member');
  }

  components.push(query);

  // Topic-specific keywords, top 2 only — beyond that the added terms start
  // outweighing the user's own words in the embedding.
  if (topic && TOPIC_KEYWORDS[topic]) {
    const keywords = TOPIC_KEYWORDS[topic].slice(0, 2);
    if (locale === 'es') {
      components.push(`relacionado con ${keywords.join(' y ')}`);
    } else {
      components.push(`related to ${keywords.join(' and ')}`);
    }
  }

  // Actionable guidance context.
  if (locale === 'es') {
    components.push('basado en orientación práctica y mejores prácticas MSP');
  } else {
    components.push('based on actionable guidance and MSP best practices');
  }

  return components.join(', ');
}

/**
 * Build a comprehensive search query with multiple keyword variations
 * for improved embedding retrieval
 *
 * This is useful for knowledge base searches that support multiple query terms
 *
 * @param query - The original user query
 * @param locale - The language locale (default: 'en')
 * @returns Array of query variations for comprehensive search
 */
export function buildQueryVariations(
  query: string,
  locale: string = 'en'
): string[] {
  const variations: string[] = [];

  variations.push(query);
  variations.push(enhanceQueryWithContext(query, undefined, locale));

  // Only worth adding when the query does not already scope itself.
  if (!query.toLowerCase().includes('wtc') && !query.toLowerCase().includes('hub')) {
    if (locale === 'es') {
      variations.push(`operaciones WTC Hub: ${query}`);
    } else {
      variations.push(`WTC Hub operations: ${query}`);
    }
  }

  const topic = detectQueryTopic(query);
  if (topic && TOPIC_KEYWORDS[topic]) {
    const keywords = TOPIC_KEYWORDS[topic];
    variations.push(`${query} ${keywords[0]}`);
  }

  return variations;
}

/**
 * Create a system context string that should be prepended to queries
 * for the knowledge base to understand the user's context
 *
 * @param locale - The language locale (default: 'en')
 * @returns Context string for knowledge base retrieval
 */
export function getQueryContextString(locale: string = 'en'): string {
  if (locale === 'es') {
    return `Contexto: El usuario es un miembro del equipo de West Tennessee Consulting (WTC) buscando orientación sobre operaciones, ventas y gestión de tickets.
Las respuestas deben ser prácticas y accionables para el negocio MSP.
Enfoque en soporte de ventas, optimización operativa y gestión de tickets.`;
  }

  return `Context: The user is a West Tennessee Consulting (WTC) team member seeking guidance on operations, sales, and ticket management.
Responses should be practical and actionable for the MSP business.
Focus on sales support, operations optimization, and ticket management.`;
}

/**
 * Create a metadata object for storing enhanced query information
 *
 * @param originalQuery - The original user query
 * @param enhancedQuery - The enhanced query
 * @param topic - Detected topic
 * @param locale - The language locale
 * @returns Metadata object
 */
export function createQueryMetadata(
  originalQuery: string,
  enhancedQuery: string,
  topic: string | null,
  locale: string = 'en'
): Record<string, unknown> {
  return {
    originalQuery,
    enhancedQuery,
    detectedTopic: topic,
    locale,
    timestamp: new Date().toISOString(),
    context: {
      platform: 'WTC Hub',
      agent: 'Meridian',
      focus: 'sales support, operations optimization, ticket management',
      expertise: 'MSP operations and CRM best practices',
    },
  };
}
